import { useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';

import { getRecordingAssetAiringDate, RecordingAsset } from '@kaltura-ott/tvpil-shared';
import { Bookmark, isFutureProgram, isLiveProgram } from '@kaltura-ott/tvpil-shared/lib/rhino/data/asset';
import { isRecordingAssetOngoing } from '@kaltura-ott/tvpil-shared/lib/rhino/data/recording/utils';

import { useCardRef, useMouseEnterHandle, useMouseLeaveHandle } from 'components/presenters/Card/hooks';
import { useAiringDate } from 'components/widgets/CardView/hooks/useAiringDate/useAiringDate';
import { useRailContext } from 'components/widgets/Rail/components/RailProvider';
import { RecordingWidgetSettings } from 'context/widgetProvider/types';
import { useWidgetContext } from 'context/widgetProvider/widgetProvider';
import { useRecordRepository } from 'hooks';
import { getAriaLabelForAsset } from 'utils';

import { getRecordingIconType, getRecordingLink, isPlayableRecording, isStandaloneRecordingAsset } from '../../utils';
import { useCardImage } from '../useCardImages';
import { useChannelInfo } from '../useChannelInfo';
import { useClickHandler } from '../useClickHandler';
import { useEpisodesCounterBadge } from '../useEpisodesCounterBadge';
import { useEpisodeTitle } from '../useEpisodeTitle';
import { useExpirationBadge } from '../useExpirationBadge';
import { useInfoClickHandler } from '../useInfoClickHandler';
import { useNewEpisodeBadge } from '../useNewEpisodeBadge';
import { useRating } from '../useRating';
import { useWatchingProgress } from '../useWatchingProgress';

type Props = {
  bookmark: Bookmark | undefined;
  recording: RecordingAsset;
  width: number;
  height: number;
};

export const useRecordingCard = ({ recording, width, height, bookmark }: Props) => {
  const intl = useIntl();
  const { settings, type, isGrid } = useWidgetContext<RecordingAsset, RecordingWidgetSettings>();
  const isProgramLive = isLiveProgram(recording);
  const isRecordOngoing = isRecordingAssetOngoing(recording);
  const isFuture = isFutureProgram(recording);
  const link = useMemo(() => getRecordingLink({ recording, isProgramLive, collectionType: type, isFuture }), [recording, type, isProgramLive, isFuture]);
  const handleClick = useClickHandler({ recording, link });
  const handleInfoClick = useInfoClickHandler({ isFuture, recording, collectionType: type });
  const { isDisplayRating, rating } = useRating(recording, settings.shouldShowRating);
  const { backgroundImageUrl, defaultImageUrl } = useCardImage({
    recording,
    imageType: settings.imageType,
    imageWidth: width,
    imageHeight: height,
  });
  const { title, channelName } = useChannelInfo(recording);
  const seasonEpisodeTitle = useEpisodeTitle(recording);
  const recordingIconType = getRecordingIconType(recording);
  const watchingProgress = useWatchingProgress(recording, bookmark);
  const expirationBadgeMessage = useExpirationBadge(recording);
  const newEpisodeBadgeMessage = useNewEpisodeBadge(recording);
  const episodesCounterMessage = useEpisodesCounterBadge(recording);
  const airingTime = getRecordingAssetAiringDate(recording);
  const handleLinkRef = useRef(null);

  // getting airingDate
  const airingDateObject = useAiringDate({ date: airingTime });
  // 

  const { onHover, nextButtons } = useRailContext();
  const { ref, handleRef } = useCardRef(width, height);
  const handlerMouseEnter = useMouseEnterHandle({
    ref,
    itemWidth: width,
    imageHeight: height,
    isGrid,
    onHover,
    nextButtons,
  });

  const handlerMouseLeave = useMouseLeaveHandle(onHover);

  // For record indication
  const recordRepository = useRecordRepository();
  const record = recording.id ? recordRepository.records.get(recording.id.toString()) : null;
  const seriesRecord = recording.seriesId ? recordRepository.seriesRecords.get(recording.seriesId) : null;
  const isRecord = Boolean(recordingIconType || record || seriesRecord);
  const isSeriesRecord = recordingIconType === 'series' || Boolean(seriesRecord);
  const isStandalone = isStandaloneRecordingAsset(recording);
  const isPlayable = isPlayableRecording(recording);
  // 

  // getting aria-label text
  const ariaLabel = getAriaLabelForAsset({
    intl,
    title,
    channelName,
    isRestricted: false,
    airingObject: airingDateObject,
    isRecord: isStandalone,
    isSeriesRecord,
    description: episodesCounterMessage,
    shouldPronouncedAiringTime: isStandalone && !isPlayable
  });

  return {
    link,
    title,
    rating,
    isRecord,
    ariaLabel,
    handleRef,
    isPlayable,
    channelName,
    handleClick,
    isStandalone,
    handleLinkRef,
    isSeriesRecord,
    handleInfoClick,
    isDisplayRating,
    defaultImageUrl,
    isRecordOngoing,
    watchingProgress,
    airingDateObject,
    handlerMouseEnter,
    handlerMouseLeave,
    backgroundImageUrl,
    seasonEpisodeTitle,
    newEpisodeBadgeMessage,
    episodesCounterMessage,
    expirationBadgeMessage,
  };
};
